import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldValues,
  get,
  Path,
  PathValue,
  useFormState,
} from 'react-hook-form';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import CalendarIcon from '@/assets/icons/calendar.svg?react';
import { format, parse, startOfToday } from 'date-fns';
import { Calendar } from '../ui/calendar';
import clsx from 'clsx';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { Input } from '../ui/input';
import { useCallback, useMemo, useState } from 'react';
import { usePastedField } from '@/contexts/PastedFieldsContext';

interface StyledDatePickerProps<FieldsType extends FieldValues> {
  control: Control<FieldsType>;
  name: Path<FieldsType>;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  defaultValue?: PathValue<FieldsType, Path<FieldsType>>;
  placeholder?: string;
  isViewMode?: boolean;
  earliestDate?: Date;
  acceptOnlyPastDates?: boolean;
  enforceCurrentDateBoundary?: boolean;
}

const StyledDatePicker = <FieldsType extends FieldValues>({
  control,
  name,
  className,
  defaultValue,
  placeholder,
  isViewMode = false,
  earliestDate,
  acceptOnlyPastDates = false,
  enforceCurrentDateBoundary = true,
}: StyledDatePickerProps<FieldsType>) => {
  const { errors } = useFormState({ control });
  const errorMessage = get(errors, name + '.message') as unknown as string;
  const [dateErrorMessage, setDateErrorMessage] = useState<string>('');
  const { isPasted, unmark } = usePastedField(name);

  return (
    <div className={clsx('flex flex-col w-full gap-1.5', className)}>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        render={({ field }) => (
          <ControllerDatePicker
            field={
              isPasted
                ? {
                    ...field,
                    onChange: (value: unknown) => {
                      unmark();
                      field.onChange(value);
                    },
                  }
                : field
            }
            isPasted={isPasted}
            isViewMode={isViewMode}
            placeholder={placeholder}
            setDateErrorMessage={setDateErrorMessage}
            earliestDate={earliestDate}
            acceptOnlyPastDates={acceptOnlyPastDates}
            enforceCurrentDateBoundary={enforceCurrentDateBoundary}
          />
        )}
      />
      {dateErrorMessage !== '' && (
        <p className="text-sm leading-6 font-medium text-error-500">
          {dateErrorMessage}
        </p>
      )}
      {dateErrorMessage === '' && errorMessage !== '' && (
        <p className="text-sm leading-6 font-medium text-error-500">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default StyledDatePicker;

const ControllerDatePicker = <FieldsType extends FieldValues>({
  field,
  isViewMode,
  placeholder,
  setDateErrorMessage,
  earliestDate,
  acceptOnlyPastDates = false,
  enforceCurrentDateBoundary = true,
  isPasted = false,
}: {
  field: ControllerRenderProps<FieldsType>;
  isPasted?: boolean;
  isViewMode: boolean;
  placeholder?: string;
  setDateErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  earliestDate?: Date;
  acceptOnlyPastDates?: boolean;
  enforceCurrentDateBoundary?: boolean;
}) => {
  const [stringDate, setStringDate] = useState<string>(
    field.value ? format(field.value, 'dd/MM/yy') : '',
  );
  const [date, setDate] = useState<Date | undefined>(field.value);

  const today = useMemo(() => startOfToday(), []);
  const boundaryDate = useMemo(() => {
    if (enforceCurrentDateBoundary) {
      return earliestDate
        ? today > earliestDate
          ? today
          : earliestDate
        : today;
    }
    return earliestDate;
  }, [earliestDate, enforceCurrentDateBoundary, today]);
  const defaultMonth = useMemo(
    () =>
      date
        ? acceptOnlyPastDates
          ? boundaryDate && boundaryDate < date
            ? boundaryDate
            : date
          : boundaryDate && boundaryDate > date
            ? boundaryDate
            : date
        : (boundaryDate ?? today),
    [acceptOnlyPastDates, boundaryDate, date, today],
  );

  const onSelectCalendarDate = useCallback(
    (selectedDate: Date | undefined) => {
      if (!selectedDate) return;
      setDate(selectedDate);
      setStringDate(format(selectedDate, 'dd/MM/yy'));
      field.onChange(selectedDate);
      setDateErrorMessage('');
    },
    [field, setDateErrorMessage],
  );

  return isViewMode ? (
    <Button
      variant="outline"
      disabled
      className={cn(
        'w-full text-left font-normal flex justify-between border-gray-300 disabled:bg-gray-100',
        !field.value && 'text-muted-foreground',
      )}
    >
      {field.value ? (
        format(field.value, 'dd/MM/yy')
      ) : (
        <span>{placeholder ?? 'JJ/MM/AA'}</span>
      )}
    </Button>
  ) : (
    <Popover>
      <div className="relative w-full">
        <Input
          type="string"
          className={cn({
            'bg-warning-50 border-warning-300 ring-2 ring-warning-200':
              isPasted,
          })}
          value={stringDate}
          placeholder={placeholder ?? 'JJ/MM/AA'}
          onChange={(e) => {
            const addedSlash =
              (e.target.value.length === 2 && stringDate.length === 1) ||
              (e.target.value.length === 5 && stringDate.length === 4)
                ? '/'
                : '';
            setStringDate(e.target.value + addedSlash);
            const parsedDate = parse(e.target.value, 'dd/MM/yy', new Date());
            if (parsedDate.toString() === 'Invalid Date') {
              setDateErrorMessage('Date invalide');
              setDate(undefined);
            } else {
              const dateMatchesBoundaries = boundaryDate
                ? !acceptOnlyPastDates
                  ? parsedDate >= boundaryDate
                  : parsedDate <= boundaryDate
                : true;

              if (dateMatchesBoundaries) {
                setDateErrorMessage('');
                setDate(parsedDate);
                field.onChange(parsedDate);
              } else {
                setDateErrorMessage('Date invalide');
                setDate(undefined);
              }
            }
          }}
        />
        <PopoverTrigger asChild>
          <Button
            variant={'outline'}
            className={cn(
              'font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none',
              !date && 'text-muted-foreground',
            )}
          >
            <CalendarIcon className="w-4 h-4 stroke-gray-500" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onSelectCalendarDate}
          disabled={
            boundaryDate
              ? (date) =>
                  acceptOnlyPastDates
                    ? date > boundaryDate
                    : date < boundaryDate
              : undefined
          }
          defaultMonth={defaultMonth}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
};
